/// <reference types="gtag.js" />

const ga_id = document.head.dataset['analyticsId']!; // eslint-disable-line @typescript-eslint/no-non-null-assertion

const configureGtag = () => {
  window.dataLayer = window.dataLayer ?? []; // eslint-disable-line @typescript-eslint/no-unnecessary-condition -- For initialization, we assume this to be undefined

  const gtag: Gtag.Gtag = function () {
    window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params -- Follow standard Google example
  };

  window.gtag = gtag;

  /**
   * Set default consent to 'denied' for all available options.
   *
   * @see {@link https://developers.google.com/tag-platform/security/guides/consent|Google consent mode documentation}
   * @see {@link https://support.google.com/analytics/answer/12334711|Consent type options for Google Analytics}
   * @see Gtag.ConsentParams
   */
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'denied',
  });
  gtag('set', 'ads_data_redaction', true);
  gtag('js', new Date());

  /**
   * Set default options as privacy-friendly as we can.
   *
   * @see {@link https://developers.google.com/analytics/devguides/collection/ga4/reference/config|Google Analytics Developer documentation}
   * @see {@link https://support.google.com/analytics/answer/13438166|Google Analytics Help on configuration settings}
   */
  gtag('config', ga_id, {
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    allow_interest_groups: false,
    cookie_flags: 'SameSite=Lax;Secure',
  });
};

const loadGAScript = () => {
  const script = document.createElement('script');
  script.defer = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${ga_id}`;
  script.type = 'text/javascript';
  document.head.appendChild(script);
};

/*
 * Outbound link tracking =============================================================
 */
const closest = (el: HTMLElement | null, selector: string) => {
  let match = el;
  while (match !== null && !match.matches(selector)) {
    match = match.parentElement;
  }

  return match;
};

// current page host
const baseURI = window.location.host;

const setupLinkTracking = () => {
  // click event on body
  document.body.addEventListener('click', (e) => {
    // abandon if link already aborted
    if (e.defaultPrevented) {
      return;
    }

    // abandon if no active link or link within domain
    const link = closest(e.target as HTMLElement, 'a') as HTMLAnchorElement | null;
    if (link === null || baseURI === link.host) {
      return;
    }

    const newWindow = link.getAttribute('target') === '_blank';

    // cancel event and record outbound link
    e.preventDefault();
    const href = link.href;

    // redirect to outbound page
    let loadPageHandled = false;
    const loadPage = () => {
      // cancel if already handled
      if (loadPageHandled) {
        return;
      }
      loadPageHandled = true;
      if (newWindow) {
        window.open(href, '_blank');
      } else {
        document.location = href;
      }
    };

    // redirect if recording takes too long
    const maxDelayInMs = 500;
    setTimeout(loadPage, maxDelayInMs);

    gtag('event', 'click', {
      event_category: 'outbound',
      event_label: href,
      event_callback: loadPage,
    });
  });
};

/*
 * Performance tracking ==================================================================
 * This sends performance data to Analytics through gtag event calls
 */
const calculateTimingMetrics = (entry: PerformanceNavigationTiming) => {
  const requestStart = entry.requestStart;
  const responseStart = entry.responseStart;
  const responseEnd = entry.responseEnd;
  const domLoading = entry.responseEnd; // see https://w3c.github.io/navigation-timing/#dom-performancetiming-domloading
  const domInteractive = entry.domInteractive;
  const navigationStart = performance.timeOrigin; // see https://w3c.github.io/navigation-timing/#dom-performancetiming-navigationstart

  return {
    requestToStartResponse: Math.round(responseStart - requestStart),
    responseTime: Math.round(responseEnd - responseStart),
    domLoadingTime: Math.round(domInteractive - domLoading),
    domInteractiveTime: Math.round(domInteractive - navigationStart),
  };
};

const sendPerformanceEvents = () => {
  const performanceEntry = window.performance?.getEntriesByType?.('navigation')[0]; // eslint-disable-line @typescript-eslint/no-unnecessary-condition -- Compatibility with legacy browsers
  if (performanceEntry) {
    const { requestToStartResponse, responseTime, domLoadingTime, domInteractiveTime } = calculateTimingMetrics(
      performanceEntry as PerformanceNavigationTiming,
    );

    // Sends the timing hit to Google Analytics.
    gtag('event', 'timing_complete', {
      name: 'requestToStartResponse',
      value: requestToStartResponse,
      event_category: 'Page',
      event_label: window.location.pathname,
    });
    if (responseTime >= 0 && responseTime < 3600) {
      gtag('event', 'timing_complete', {
        name: 'response',
        value: responseTime,
        event_category: 'Page',
        event_label: window.location.pathname,
      });
    }
    gtag('event', 'timing_complete', {
      name: 'domLoading',
      value: domLoadingTime,
      event_category: 'Page',
      event_label: window.location.pathname,
    });
    gtag('event', 'timing_complete', {
      name: 'domInteractive',
      value: domInteractiveTime,
      event_category: 'Page',
      event_label: window.location.pathname,
    });
  }
};

const gpcEnabled = navigator.globalPrivacyControl ?? false;

if (!gpcEnabled) {
  loadGAScript();
  configureGtag();
  window.addEventListener('DOMContentLoaded', () => {
    setupLinkTracking();
    sendPerformanceEvents();
  });
};

declare global {
  interface Navigator {
    // polyfills https://developer.mozilla.org/en-US/docs/Web/API/Navigator/globalPrivacyControl
    globalPrivacyControl?: boolean;
  }
  interface Window {
    dataLayer: Array<unknown>;
    gtag: Gtag.Gtag;
  }
}
